import type {
    RepeatableDomainAdapterContext,
    RepeatableGroupConfiguration,
    RepeatableGroupDomainAdapter,
    RepeatableInfoDialogTable,
    RepeatableInfoDialogView,
    RepeatableRowExtraItem,
    RepeatableRowExtraView,
} from '../../../interfaces/input.interface';

export type RepeatableDomainStatus = 'valid' | 'invalid' | 'unknown';

type DoseRange = {
    min: number;
    max: number | null;
    unit: string;
};

const TIPO_ABONO_ORGANICO_LABELS: Record<string, string> = {
    EB: 'Estiércol de bovino',
    EO: 'Estiércol de ovino',
    EP: 'Estiércol de porcino',
    PP: 'Purines de porcino',
    G: 'Gallinaza',
    L: 'Lodos de depuradora',
    C: 'Compost de RSU',
    O: 'Otros',
};

const MATERIAL_FERTILIZANTE_LABELS: Record<string, string> = {
    '1': 'Estiercol líquido de aves',
    '2': 'Estiércol líquido de bovino',
    '3': 'Estiércol líquido de porcino',
    '4': 'Estiércol sólido de aves',
    '5': 'Estiércol sólido de bovino',
    '6': 'Estiércol sólido de caprino',
    '7': 'Estiércol sólido de equino',
    '8': 'Estiércol sólido de ovino',
    '9': 'Inhibidores',
    '10': 'Otros tipos de estiércol sólido: conejo, visón, zorro…',
    '11': 'Productos fertilizantes bioestimulantes',
    '12': 'Productos fertilizantes enmiendas calizas',
    '13': 'Productos fertilizantes enmiendas orgánicas',
    '14': 'Productos fertilizantes inorgánicos',
    '15': 'Productos fertilizantes orgánicos',
    '16': 'Productos fertilizantes organominerales',
    '17': 'Productos fertilizantes otras enmiendas',
    '18': 'Residuos: Materiales sin empleo en la elaboración de fertilizante',
    '19': 'Residuos: Subproductos de origen animal no aptos para consumo humano',
    '20': 'Residuos: compost de alperujo',
    '21': 'Residuos: alperujo desecado',
    '22': 'Residuos: Lodos EDAR',
    '23': 'Residuos: Lodos calizos del proceso Kraft',
};

export class RepeatableDomainAdapterFactory {
    public static create(configuration: RepeatableGroupConfiguration): RepeatableGroupDomainAdapter {
        if (configuration.domainAdapter) {
            return configuration.domainAdapter;
        }

        if (this.looksLikeFitosanitariosProductGroup(configuration)) {
            return new FitosanitariosRepeatableDomainAdapter();
        }

        return new EmptyRepeatableDomainAdapter();
    }

    private static looksLikeFitosanitariosProductGroup(configuration: RepeatableGroupConfiguration): boolean {
        const keys = new Set((configuration.subFields ?? []).map((subField) => subField.key));

        return keys.has('producto') && keys.has('uso') && keys.has('agente') && keys.has('dosisCuaderno');
    }
}

export class EmptyRepeatableDomainAdapter implements RepeatableGroupDomainAdapter {
    public getNumericInputClasses(): Record<string, boolean> {
        return {};
    }

    public getRowExtraView(): RepeatableRowExtraView {
        return {
            isVisible: false,
            items: [],
        };
    }

    public canOpenInfoDialog(): boolean {
        return false;
    }

    public getInfoDialogView(): RepeatableInfoDialogView | null {
        return null;
    }
}

export class FitosanitariosRepeatableDomainAdapter implements RepeatableGroupDomainAdapter {
    public getNumericInputClasses(ctx: RepeatableDomainAdapterContext, key: string): Record<string, boolean> {
        if (key !== 'dosisCuaderno') {
            return {};
        }

        const status = this.getDoseStatus(ctx);

        return {
            'dose-input': true,
            'dose-input-valid': status === 'valid',
            'dose-input-invalid': status === 'invalid',
        };
    }

    public getRowExtraView(ctx: RepeatableDomainAdapterContext): RepeatableRowExtraView {
        if (!this.shouldShowDoseRecommendation(ctx)) {
            return {
                isVisible: false,
                items: [],
            };
        }

        const status = this.getDoseStatus(ctx);
        const items = this.filterVisibleItems([
            { label: 'Dosis', value: this.getDoseText(ctx), styleClass: 'dose-range' },
            { label: 'Estado', value: this.getStatusText(status), styleClass: 'dose-status' },
            { label: 'Plazo seg.', value: this.getAgentText(ctx, 'plazo_seguridad') },
            { label: 'Intervalos', value: this.getAgentText(ctx, 'intervalos') },
            { label: 'N.º máx.', value: this.getAgentText(ctx, 'n_aplicaciones') },
            { label: 'Vol. caldo', value: this.getAgentText(ctx, 'vol_caldo') },
        ]);

        return {
            isVisible: items.length > 0,
            styleClass: this.getStatusClass(status),
            items,
        };
    }

    public canOpenInfoDialog(ctx: RepeatableDomainAdapterContext): boolean {
        return ctx.resolveEntity('producto') !== null;
    }

    public getInfoDialogView(ctx: RepeatableDomainAdapterContext): RepeatableInfoDialogView | null {
        const product = ctx.resolveEntity('producto');

        if (!product) {
            return null;
        }

        if (this.isFertilizer(product)) {
            return this.getFertilizerInfoDialogView(ctx, product);
        }

        return this.getTreatmentInfoDialogView(ctx, product);
    }

    private getTreatmentInfoDialogView(ctx: RepeatableDomainAdapterContext, product: Record<string, unknown>): RepeatableInfoDialogView {
        const uso = ctx.resolveEntity('uso');
        const agente = ctx.resolveEntity('agente');
        const nestedProduct = this.readObjectRecord(product['producto']);
        const condicionesUso = this.readFirstNonEmptyString(product, ['condiciones_uso'])
            || this.readFirstNonEmptyString(nestedProduct, ['condiciones_uso'])
            || '-';

        return {
            header: `Información Producto ${this.getProductName(product)}`.trim(),
            sections: [
                {
                    tables: [
                        this.createTable(
                            ['Producto', 'Fecha de caducidad', 'Materia activa', 'Condiciones de uso'],
                            [[
                                this.getProductName(product),
                                this.readFirstNonEmptyString(product, ['fecha_caducidad']) || '-',
                                this.readFirstNonEmptyString(product, ['composicion', 'materia_activa']) || '-',
                                condicionesUso,
                            ]],
                            'product-info-table',
                        ),
                        this.createTable(
                            [
                                'Uso',
                                'Agente',
                                'Dosis mínima',
                                'Dosis máxima',
                                'Dosis aplicada',
                                'Ud. medida',
                                'Vol. caldo',
                                'Forma y época',
                                'N.º máx.',
                                'Intervalos',
                                'Plazo seg.',
                            ],
                            [[
                                this.readFirstNonEmptyString(uso, ['uso']) || '-',
                                this.readFirstNonEmptyString(agente, ['agente']) || '-',
                                this.getDoseMinText(ctx),
                                this.getDoseMaxText(ctx),
                                this.getAppliedDoseText(ctx),
                                this.getUnitText(ctx),
                                this.getAgentText(ctx, 'vol_caldo') || '-',
                                this.readFirstNonEmptyString(uso, ['forma_epoca']) || this.readFirstNonEmptyString(agente, ['forma_epoca']) || '-',
                                this.getAgentText(ctx, 'n_aplicaciones') || '-',
                                this.getAgentText(ctx, 'intervalos') || '-',
                                this.getAgentText(ctx, 'plazo_seguridad') || '-',
                            ]],
                            'product-info-table product-treatment-table',
                        ),
                    ],
                },
            ],
        };
    }

    private getFertilizerInfoDialogView(ctx: RepeatableDomainAdapterContext, product: Record<string, unknown>): RepeatableInfoDialogView {
        return {
            header: `Información Fertilizante ${this.getProductName(product)}`.trim(),
            sections: [
                {
                    tables: [
                        this.createTable(
                            ['Producto', 'Descripción', 'Composición', 'Ud. medida', 'Envase'],
                            [[
                                this.getProductName(product),
                                this.productText(product, ['descripcion']),
                                this.productText(product, ['composicion']),
                                this.productText(product, ['unidades']),
                                this.productText(product, ['envase']),
                            ]],
                            'product-info-table',
                        ),
                        this.createTable(
                            ['Tipo Abono Orgánico', 'Abono con inhibidor', 'N%', 'P%', 'K%', 'Ca%', 'S%', 'Mg%'],
                            [[
                                this.getFertiTipoAbonoOrganicoText(product),
                                this.getFertiAbonoConInhibidorText(product),
                                this.fertiFieldText(product, 'N'),
                                this.fertiFieldText(product, 'P'),
                                this.fertiFieldText(product, 'K'),
                                this.fertiFieldText(product, 'Ca'),
                                this.fertiFieldText(product, 'S'),
                                this.fertiFieldText(product, 'Mg'),
                            ]],
                            'product-info-table product-fertilizer-table',
                        ),
                        this.createTable(
                            [
                                'Material Fertilizante',
                                'N Total',
                                'N Orgánico',
                                'N Nítrico',
                                'N Amoniacal',
                                'N Ureico',
                                'P2O5 Total',
                                'P2O5 Agua',
                                'P2O5 Citrato',
                                'K20 Total',
                                'Materia orgánica',
                                'Calcio (CaO)',
                                'Nombre del tipo',
                                'Dosis',
                                'Tratamiento recibido',
                            ],
                            [[
                                this.getFertiMaterialFertiText(product),
                                this.fertiFieldText(product, 'n_total'),
                                this.fertiFieldText(product, 'n_organico'),
                                this.fertiFieldText(product, 'n_nitrico'),
                                this.fertiFieldText(product, 'n_amoniacal'),
                                this.fertiFieldText(product, 'n_ureico'),
                                this.fertiFieldText(product, 'p2o5_total'),
                                this.fertiFieldText(product, 'p2o5_agua'),
                                this.fertiFieldText(product, 'p2o5_amonico'),
                                this.fertiFieldText(product, 'k20_total'),
                                this.fertiFieldText(product, 'materia_organica'),
                                this.fertiFieldText(product, 'calcio'),
                                this.fertiFieldText(product, 'nombre_tipo'),
                                this.fertiFieldText(product, 'dosis'),
                                this.fertiFieldText(product, 'tratamiento_recibido'),
                            ]],
                            'product-info-table product-fertilizer-table product-fertilizer-table-wide',
                        ),
                        this.createTable(
                            [
                                'Dosis aplicada',
                                'Ud. medida',
                                'Cadmio (Cd)',
                                'Cobre (Cu)',
                                'Plomo (Pb)',
                                'Níquel (Ni)',
                                'Zinc (Zn)',
                                'Mercurio (Hg)',
                                'Cromo total (Cr)',
                            ],
                            [[
                                this.getAppliedDoseText(ctx),
                                this.getUnitText(ctx),
                                this.fertiFieldText(product, 'm_cadmio'),
                                this.fertiFieldText(product, 'm_cobre'),
                                this.fertiFieldText(product, 'm_plomo'),
                                this.fertiFieldText(product, 'm_niquel'),
                                this.fertiFieldText(product, 'm_zinc'),
                                this.fertiFieldText(product, 'm_mercurio'),
                                this.fertiFieldText(product, 'm_cromo_total'),
                            ]],
                            'product-info-table product-fertilizer-table',
                        ),
                    ],
                },
            ],
        };
    }

    private shouldShowDoseRecommendation(ctx: RepeatableDomainAdapterContext): boolean {
        const agent = ctx.resolveEntity('agente');
        const uso = ctx.resolveEntity('uso');

        if (!agent) {
            return false;
        }

        const usoLabel = String(uso?.['uso'] ?? '').trim();
        const agentLabel = String(agent?.['agente'] ?? '').trim();

        return agentLabel !== '' && usoLabel !== '-';
    }

    private getDoseText(ctx: RepeatableDomainAdapterContext): string {
        const range = this.getDoseRange(ctx);

        if (!range) {
            return '';
        }

        const minText = this.formatDoseNumber(range.min);
        const unitText = range.unit !== '' ? ` ${range.unit}` : '';

        if (range.max !== null && range.max > 0) {
            return `${minText} - ${this.formatDoseNumber(range.max)}${unitText}`;
        }

        return `${minText}${unitText}`;
    }

    private getDoseMinText(ctx: RepeatableDomainAdapterContext): string {
        const range = this.getDoseRange(ctx);

        return range ? this.formatDoseNumber(range.min) : '-';
    }

    private getDoseMaxText(ctx: RepeatableDomainAdapterContext): string {
        const range = this.getDoseRange(ctx);

        if (!range || range.max === null) {
            return '-';
        }

        return this.formatDoseNumber(range.max);
    }

    private getDoseStatus(ctx: RepeatableDomainAdapterContext): RepeatableDomainStatus {
        const range = this.getDoseRange(ctx);

        if (!range) {
            return 'unknown';
        }

        const doseValue = this.parseUnknownNumber(ctx.row['dosisCuaderno']);

        if (doseValue === null) {
            return 'unknown';
        }

        if (range.max !== null && range.max > 0) {
            return doseValue >= range.min && doseValue <= range.max ? 'valid' : 'invalid';
        }

        return doseValue >= range.min ? 'valid' : 'invalid';
    }

    private getDoseRange(ctx: RepeatableDomainAdapterContext): DoseRange | null {
        const agent = ctx.resolveEntity('agente');

        if (!agent) {
            return null;
        }

        const min = this.firstFiniteNumber([
            agent['minDosis'],
            agent['dosis_min'],
            agent['dosis1'],
        ]);

        if (min === null || min <= 0) {
            return null;
        }

        const max = this.firstFiniteNumber([
            agent['maxDosis'],
            agent['dosis_max'],
            agent['dosis2'],
        ]);
        const product = ctx.resolveEntity('producto');
        const unit = this.normalizeDoseUnit(agent['unidad_medida'] ?? product?.['unidades'] ?? '');

        return {
            min,
            max: max !== null && max > 0 ? max : null,
            unit,
        };
    }

    private getStatusText(status: RepeatableDomainStatus): string {
        switch (status) {
            case 'valid':
                return 'Dentro de rango';

            case 'invalid':
                return 'Fuera de rango';

            case 'unknown':
                return '';
        }
    }

    private getStatusClass(status: RepeatableDomainStatus): string {
        switch (status) {
            case 'valid':
                return 'dose-recommendation-valid';

            case 'invalid':
                return 'dose-recommendation-invalid';

            case 'unknown':
                return 'dose-recommendation-neutral';
        }
    }

    private getAgentText(ctx: RepeatableDomainAdapterContext, key: string): string {
        const agent = ctx.resolveEntity('agente');

        return String(agent?.[key] ?? '').trim();
    }

    private getAppliedDoseText(ctx: RepeatableDomainAdapterContext): string {
        const dosisAplicada = this.parseUnknownNumber(ctx.row['dosisCuaderno']);

        return dosisAplicada === null ? '-' : this.formatDoseNumber(dosisAplicada);
    }

    private getUnitText(ctx: RepeatableDomainAdapterContext): string {
        const range = this.getDoseRange(ctx);

        if (range?.unit) {
            return range.unit;
        }

        const agent = ctx.resolveEntity('agente');
        const product = ctx.resolveEntity('producto');

        return this.readFirstNonEmptyString(agent, ['unidad_medida']) || this.readFirstNonEmptyString(product, ['unidades']) || '-';
    }

    private isFertilizer(product: Record<string, unknown>): boolean {
        return String(product['tipo'] ?? '').trim().toLowerCase() === 'fertilizante';
    }

    private getProductName(product: Record<string, unknown>): string {
        return this.readFirstNonEmptyString(product, ['nombre_comercial', 'nombre']) || '-';
    }

    private productText(product: Record<string, unknown>, keys: string[]): string {
        return this.readFirstNonEmptyString(product, keys) || '-';
    }

    private fertiFieldText(product: Record<string, unknown>, key: string): string {
        return this.readFirstNonEmptyString(product, [key]) || '-';
    }

    private getFertiTipoAbonoOrganicoText(product: Record<string, unknown>): string {
        const raw = this.readFirstNonEmptyString(product, ['tipo_abono_organico']);

        return raw === '' ? '-' : TIPO_ABONO_ORGANICO_LABELS[raw] ?? raw;
    }

    private getFertiAbonoConInhibidorText(product: Record<string, unknown>): string {
        const value = product['abono_con_inhibidor'];

        if (value === null || value === undefined || this.isNullishStringToken(String(value))) {
            return '-';
        }

        return this.coerceBoolean(value) ? 'Sí' : 'No';
    }

    private getFertiMaterialFertiText(product: Record<string, unknown>): string {
        const raw = this.readFirstNonEmptyString(product, ['material_ferti']);

        return raw === '' ? '-' : MATERIAL_FERTILIZANTE_LABELS[raw] ?? raw;
    }

    private filterVisibleItems(items: RepeatableRowExtraItem[]): RepeatableRowExtraItem[] {
        return items.filter((item) => item.value.trim() !== '');
    }

    private createTable(headers: string[], rows: string[][], styleClass: string): RepeatableInfoDialogTable {
        return {
            headers,
            rows,
            styleClass,
        };
    }

    private readFirstNonEmptyString(record: Record<string, unknown> | null | undefined, keys: string[]): string {
        if (!record) {
            return '';
        }

        for (const key of keys) {
            const raw = record[key];

            if (raw === null || raw === undefined) {
                continue;
            }

            const text = String(raw).trim();

            if (text !== '' && !this.isNullishStringToken(text)) {
                return text;
            }
        }

        return '';
    }

    private readObjectRecord(value: unknown): Record<string, unknown> | null {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            return value as Record<string, unknown>;
        }

        return null;
    }

    private coerceBoolean(value: unknown): boolean {
        return value === true || value === 1 || String(value).trim() === '1';
    }

    private firstFiniteNumber(values: unknown[]): number | null {
        for (const value of values) {
            const parsed = this.parseUnknownNumber(value);

            if (parsed !== null) {
                return parsed;
            }
        }

        return null;
    }

    private parseUnknownNumber(value: unknown): number | null {
        if (typeof value === 'number') {
            return Number.isFinite(value) ? value : null;
        }

        const text = String(value ?? '').trim();

        if (text === '' || this.isNullishStringToken(text)) {
            return null;
        }

        return this.parseFlexibleNumber(text);
    }

    private parseFlexibleNumber(token: string): number | null {
        const normalized = token
            .replace(/\s/g, '')
            .replace(/\.(?=\d{3}(\D|$))/g, '')
            .replace(',', '.');
        const parsed = Number(normalized);

        return Number.isFinite(parsed) ? parsed : null;
    }

    private formatDoseNumber(value: number): string {
        return value.toFixed(2).replace('.', ',');
    }

    private normalizeDoseUnit(value: unknown): string {
        const unit = String(value ?? '').trim().toLowerCase();

        switch (unit) {
            case 'kg':
            case 'kilo':
            case 'kilos':
            case 'kilogramo':
            case 'kilogramos':
            case 'kg/ha':
                return 'kg/ha';

            case 'l':
            case 'litro':
            case 'litros':
            case 'l/ha':
                return 'L/ha';

            default:
                return String(value ?? '').trim();
        }
    }

    private isNullishStringToken(value: string): boolean {
        const token = value.trim().toLowerCase();

        return token === 'null' || token === 'undefined' || token === 'nan' || token === 'invalid date';
    }
}
